import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Play } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

import blogBriefingBrand from "@/assets/blog/blog-briefing-brand.jpg";
import blogCateringTrends from "@/assets/blog/blog-catering-trends.jpg";
import blogChefConversation from "@/assets/blog/blog-chef-conversation.jpg";
import blogDispatchCabin from "@/assets/blog/blog-dispatch-cabin.jpg";
import blogGalleyHandoff from "@/assets/blog/blog-galley-handoff.jpg";
import blogKitchensAirports from "@/assets/blog/blog-kitchens-airports.jpg";
import blogMenusAltitude from "@/assets/blog/blog-menus-altitude.jpg";
import blogMultiLegOrders from "@/assets/blog/blog-multi-leg-orders.jpg";
import blogPaymentsCheckout from "@/assets/blog/blog-payments-checkout.jpg";

type StoryTab = "product" | "industry" | "conversations";

type StoryCard = {
  category: string;
  title: string;
  excerpt: string;
  date: string;
  imageSrc: string;
  imageAlt: string;
  href: string;
};

const TABS: { id: StoryTab; label: string }[] = [
  { id: "product", label: "Product" },
  { id: "industry", label: "Industry" },
  { id: "conversations", label: "Conversations" },
];

const STORIES: Record<StoryTab, StoryCard[]> = {
  product: [
    {
      category: "Product",
      title: "New: Multi-leg orders are now live",
      excerpt: "Build entire trips in one workspace.",
      date: "May 6, 2026",
      imageSrc: blogMultiLegOrders,
      imageAlt: "Flight attendant planning a multi-leg catering order on a tablet",
      href: "/blog",
    },
    {
      category: "Product",
      title: "Payments that match how crews operate",
      excerpt: "Stripe-backed checkout with clear receipts and fewer back-and-forth messages.",
      date: "April 18, 2026",
      imageSrc: blogPaymentsCheckout,
      imageAlt: "Secure mobile payment checkout for a private aviation catering order",
      href: "/blog",
    },
    {
      category: "Product",
      title: "Menus that stay legible at 40,000 ft",
      excerpt: "Structured catalogues, allergens, and notes that travel with the order.",
      date: "March 22, 2026",
      imageSrc: blogMenusAltitude,
      imageAlt: "Gourmet plated meals and structured menus prepared for private jet catering",
      href: "/blog",
    },
  ],
  industry: [
    {
      category: "Industry",
      title: "Catering trends in private aviation",
      excerpt: "What operators are seeing in 2026.",
      date: "May 2, 2026",
      imageSrc: blogCateringTrends,
      imageAlt: "Private jet on the tarmac with catering logistics on the apron",
      href: "/blog",
    },
    {
      category: "Industry",
      title: "Dispatch and cabin: closing the feedback loop",
      excerpt: "How leading flight departments align expectations before wheels up.",
      date: "April 9, 2026",
      imageSrc: blogDispatchCabin,
      imageAlt: "Dispatch and cabin crew reviewing a flight briefing together",
      href: "/blog",
    },
    {
      category: "Industry",
      title: "Scaling trusted kitchens across airports",
      excerpt: "Quality signals operators use when onboarding new caterers.",
      date: "March 4, 2026",
      imageSrc: blogKitchensAirports,
      imageAlt: "Chefs preparing orders in a professional airport catering kitchen",
      href: "/blog",
    },
  ],
  conversations: [
    {
      category: "Conversations",
      title: "Inside Harry Traiteur with Chef Harry",
      excerpt: "A conversation on craft and consistency.",
      date: "April 28, 2026",
      imageSrc: blogChefConversation,
      imageAlt: "Executive chef in a professional kitchen for a Kryajet conversation feature",
      href: "/blog",
    },
    {
      category: "Conversations",
      title: "From galley timing to ground handoff",
      excerpt: "CFAs and chefs on what actually prevents surprises.",
      date: "April 11, 2026",
      imageSrc: blogGalleyHandoff,
      imageAlt: "Catering handoff between chef and flight attendant at aircraft stairs",
      href: "/blog",
    },
    {
      category: "Conversations",
      title: "The briefing that protects the brand",
      excerpt: "Why operators standardise notes when cabin expectations shift.",
      date: "February 19, 2026",
      imageSrc: blogBriefingBrand,
      imageAlt: "Operations team in a briefing meeting reviewing service standards",
      href: "/blog",
    },
  ],
};

export function LandingStoriesSection() {
  const [tab, setTab] = useState<StoryTab>("product");
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const cards = useMemo(() => STORIES[tab], [tab]);

  const onNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({
        variant: "destructive",
        title: "Check your email",
        description: "Enter a valid email address to subscribe.",
      });
      return;
    }
    toast({
      title: "You are on the list",
      description: "We will only send the Kryajet Brief — one email a month.",
    });
    setEmail("");
  };

  return (
    <section id="stories" className="border-y border-border/60 bg-muted/20 py-16 md:py-24">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Main column */}
          <div className="lg:col-span-8">
            <h2 className="text-balance text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
              Stories from the cockpit, the kitchen, and everywhere in between.
            </h2>

            <div className="mt-8 flex flex-wrap gap-8 border-b border-border/80 pb-2">
              {TABS.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={cn(
                    "-mb-px border-b-2 pb-2 text-sm font-medium transition-colors",
                    tab === id
                      ? "border-accent text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map((story) => (
                <Link
                  key={`${tab}-${story.title}`}
                  to={story.href}
                  className="group flex flex-col overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm transition hover:border-accent/40 hover:shadow-md"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-muted">
                    <img
                      src={story.imageSrc}
                      alt={story.imageAlt}
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-accent">{story.category}</p>
                    <h3 className="mt-2 line-clamp-2 text-base font-semibold leading-snug text-foreground group-hover:underline">
                      {story.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 flex-1 text-sm text-muted-foreground">{story.excerpt}</p>
                    <p className="mt-3 text-xs text-muted-foreground">{story.date}</p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-10">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground underline-offset-4 hover:underline"
              >
                View all articles
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="flex flex-col gap-6 lg:col-span-4">
            <div className="rounded-xl border border-border/80 bg-card p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-foreground">Get the Kryajet Brief</h3>
              <p className="mt-2 text-sm text-muted-foreground">1 email a month. No fluff.</p>
              <form onSubmit={onNewsletterSubmit} className="mt-4 space-y-3">
                <Input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-background"
                />
                <Button type="submit" className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                  Subscribe
                </Button>
              </form>
            </div>

            <div className="overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm">
              <h3 className="border-b border-border/60 px-6 py-4 text-lg font-semibold text-foreground">
                Latest podcast episode
              </h3>
              <Link
                to="/blog"
                className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className="relative aspect-video bg-muted">
                  <img
                    src={blogChefConversation}
                    alt="Podcast episode cover featuring a chef interview"
                    className="h-full w-full object-cover opacity-90 blur-[1px] transition group-hover:opacity-100 group-hover:blur-none"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/25 transition group-hover:bg-black/20">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-foreground shadow-lg">
                      <Play className="h-7 w-7 fill-current pl-1" aria-hidden />
                      <span className="sr-only">Play episode</span>
                    </span>
                  </div>
                </div>
                <div className="space-y-2 p-6">
                  <p className="text-sm font-medium leading-snug text-foreground group-hover:underline">
                    Ep. 12 · Building better catering ops with Max &amp; guests
                  </p>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    25 min
                  </p>
                </div>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
